import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppProvider } from './context/AppContext';
import AuthPage from './components/auth/AuthPage';
import Layout from './components/layout/Layout';
import DashboardPage from './components/dashboard/DashboardPage';
import IntegrationDashboard from './components/dashboard/IntegrationDashboard';
import SettingsPage from './components/settings/SettingsPage';
import HierarchicalStructure from './pages/HierarchicalStructure';
import ExecutionUnitsPage from './pages/ExecutionUnitsPage';
import BookingsPage from './pages/BookingsPage';
import DiariesPage from './pages/DiariesPage';
import DiarySyncPage from './pages/DiarySyncPage';
import SyncPage from './pages/SyncPage';
import NotificationsPage from './pages/NotificationsPage';
import UsersPage from './pages/UsersPage';
import TestEndpointsPage from './pages/TestEndpointsPage';
import AuthoritySyncPage from './pages/AuthoritySyncPage';
import SitesPage from './pages/SitesPage';
import HospitalsPage from './pages/HospitalsPage';
import OperationUnitsPage from './pages/OperationUnitsPage';
import TestRoutingPage from './pages/TestRoutingPage';
import BaseDataSyncPage from './pages/BaseDataSyncPage';
import DoctorsPage from './pages/DoctorsPage';
import ExemptionsPage from './pages/ExemptionsPage';
import HealthcareServicesPage from './pages/HealthcareServicesPage';
import PrescriptionsPage from './pages/PrescriptionsPage';
import ProfilesPage from './components/pages/ProfilesPage';
import CalendarPage from './pages/CalendarPage';

// Tema Material-UI personalizzato
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

// Componente per route protette
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        Loading...
      </Box>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Componente principale dell'app
const AppContent: React.FC = () => {
  const { isAuthenticated, login } = useAuth();

  const handleLogin = async (username: string, password: string) => {
    try {
      await login(username, password);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleRegister = async (username: string, email: string, password: string) => {
    // TODO: Implement registration logic
    console.log('Register:', { username, email, password });
  };

  const handleUpdateSettings = (settings: any) => {
    // TODO: Implement settings update logic
    console.log('Update settings:', settings);
  };

  return (
    <Routes>
      {/* Route di autenticazione */}
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> :
          <AuthPage onLogin={handleLogin} onRegister={handleRegister} />
        }
      />
      
      {/* Route protette */}
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        {/* Redirect root a dashboard */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        
        {/* Dashboard principale */}
        <Route path="dashboard" element={<DashboardPage />} />

        {/* Calendario Appuntamenti */}
        <Route path="calendar" element={<CalendarPage />} />

        {/* Dashboard integrazione */}
        <Route path="integration" element={<IntegrationDashboard />} />
        
        {/* Struttura Gerarchica */}
        <Route path="hierarchical-structure" element={<HierarchicalStructure />} />
        
        {/* Sites */}
        <Route path="sites" element={<SitesPage />} />
        
        {/* Hospitals */}
        <Route path="hospitals" element={<HospitalsPage />} />
        
        {/* Operation Units */}
        <Route path="operation-units" element={<OperationUnitsPage />} />
        
        {/* Unità Eroganti */}
        <Route path="execution-units" element={<ExecutionUnitsPage />} />
        
        {/* Sincronizzazione Agende */}
        <Route path="diary-sync" element={<DiarySyncPage />} />
        
        {/* Gestione prenotazioni */}
        <Route path="bookings" element={<BookingsPage />} />
        
        {/* Agende */}
        <Route path="diaries" element={<DiariesPage />} />
        
        {/* Sincronizzazione */}
        <Route path="sync" element={<SyncPage />} />
        
        {/* Sincronizzazione Dati di Base */}
        <Route path="base-data-sync" element={<BaseDataSyncPage />} />
        
        {/* Monitoraggio */}
        <Route path="monitoring" element={<div>Monitoraggio Avanzato (Coming Soon)</div>} />
        
        {/* Conflitti */}
        <Route path="conflicts" element={<div>Gestione Conflitti (Coming Soon)</div>} />
        
        {/* Notifiche */}
        <Route path="notifications" element={<NotificationsPage />} />
        
        {/* Utenti */}
        <Route path="users" element={<UsersPage />} />
        
        {/* Impostazioni */}
        <Route path="settings" element={<SettingsPage onUpdateSettings={handleUpdateSettings} />} />
        
        {/* Test Endpoints */}
        <Route path="test-endpoints" element={<TestEndpointsPage />} />
        
        {/* Authority Sync */}
        <Route path="authority-sync-full" element={<AuthoritySyncPage />} />
        
        {/* Doctors */}
        <Route path="doctors" element={<DoctorsPage />} />
        
        {/* Exemptions */}
        <Route path="exemptions" element={<ExemptionsPage />} />
        
        {/* Healthcare Services */}
        <Route path="healthcare-services" element={<HealthcareServicesPage />} />
        
        {/* Test Routing Simple */}
        <Route path="test-routing-simple" element={<TestRoutingPage />} />
        
        {/* Prescriptions */}
        <Route path="prescriptions" element={<PrescriptionsPage />} />
        
        {/* Profiles */}
        <Route path="profiles" element={<ProfilesPage />} />
        
        {/* Reports */}
        <Route path="reports" element={<div>Reports (Coming Soon)</div>} />
        
        {/* Logs */}
        <Route path="logs" element={<div>Visualizzazione Logs (Coming Soon)</div>} />
      </Route>

      {/* Route di fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

// Componente App principale
const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppProvider>
          <Router>
            <AppContent />
          </Router>
        </AppProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;